import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Upload, Download, FileSpreadsheet } from 'lucide-react';

export const BulkProductImport = () => {
  const [uploading, setUploading] = useState(false);
  const [file, setFile] = useState<File | null>(null);

  const downloadTemplate = () => {
    const headers = ['name', 'model', 'description', 'unit_price', 'quantity', 'listing_type', 'product_link'];
    const csvContent = headers.join(',') + '\n' + 
      'Example Product,M132,Smart toilet with advanced features,450.00,100,retail,https://example.com/product\n' +
      'Wholesale Batch,M158,Bulk order smart toilets,380.00,500,b2b_wholesale,https://example.com/wholesale';
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'product_import_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile && selectedFile.type === 'text/csv') {
      setFile(selectedFile);
    } else {
      toast.error('Please select a valid CSV file');
    }
  };

  const parseCSV = (text: string): any[] => {
    const lines = text.split('\n').filter(line => line.trim());
    if (lines.length < 2) return [];
    
    const headers = lines[0].split(',').map(h => h.trim());
    const products = [];

    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',').map(v => v.trim());
      const product: any = {};
      
      headers.forEach((header, index) => {
        if (values[index]) {
          if (header === 'unit_price' || header === 'quantity') {
            product[header] = parseFloat(values[index]) || 0;
          } else {
            product[header] = values[index];
          }
        }
      });

      if (product.name && product.unit_price) {
        products.push(product);
      }
    }

    return products;
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error('Please select a CSV file');
      return;
    }

    setUploading(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error('You must be logged in to upload products');
        return;
      }

      const text = await file.text();
      const products = parseCSV(text);

      if (products.length === 0) {
        toast.error('No valid products found in CSV');
        return;
      }

      const productsWithAgent = products.map(p => ({
        ...p,
        agent_id: user.id,
        status: 'pending',
        total_price: p.unit_price * (p.quantity || 1)
      }));

      const { error } = await supabase
        .from('products')
        .insert(productsWithAgent);

      if (error) throw error;

      toast.success(`Successfully uploaded ${products.length} products`);
      setFile(null);
      
      // Reset file input
      const fileInput = document.getElementById('csv-upload') as HTMLInputElement;
      if (fileInput) fileInput.value = '';
      
    } catch (error: any) {
      console.error('Upload error:', error);
      toast.error(error.message || 'Failed to upload products');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileSpreadsheet className="h-5 w-5" />
          Bulk Product Import
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-col gap-4">
          <div>
            <p className="text-sm text-muted-foreground mb-2">
              Upload a CSV file to import multiple products at once. Make sure your CSV follows the template format.
            </p>
            <Button onClick={downloadTemplate} variant="outline" className="w-full sm:w-auto">
              <Download className="h-4 w-4 mr-2" />
              Download CSV Template
            </Button>
          </div>

          <div className="space-y-2">
            <Input
              id="csv-upload"
              type="file"
              accept=".csv"
              onChange={handleFileChange}
            />
            {file && (
              <p className="text-sm text-muted-foreground">
                Selected: {file.name}
              </p>
            )}
          </div>

          <Button 
            onClick={handleUpload} 
            disabled={!file || uploading}
            className="w-full sm:w-auto"
          >
            <Upload className="h-4 w-4 mr-2" />
            {uploading ? 'Uploading...' : 'Upload Products'}
          </Button>
        </div>

        <div className="bg-muted/50 p-4 rounded-lg text-sm space-y-1">
          <p className="font-medium">CSV Format Requirements:</p>
          <ul className="list-disc list-inside space-y-1 text-muted-foreground">
            <li>First row must contain headers</li>
            <li>Required columns: name, unit_price</li>
            <li>Optional: model, description, quantity, listing_type (retail/b2b_wholesale), product_link</li>
            <li>listing_type defaults to 'retail' if not specified</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
};
